<template>
  <!-- id="app" fica só no index.html; duplicar quebra HTML e confunde DevTools/extensões -->
  <div class="app-root min-h-screen bg-neutral-50 dark:bg-neutral-900">
    <!-- Main Router View -->
    <router-view v-slot="{ Component, route }">
      <transition
        :name="route.meta.transition || 'fade'"
        mode="out-in"
        appear
      >
        <component :is="Component" :key="route.path" />
      </transition>
    </router-view>

    <!-- Global Loading State -->
    <Teleport to="body">
      <div
        v-if="isLoading"
        class="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/50 backdrop-blur-sm"
      >
        <div class="flex flex-col items-center space-y-4">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          <p class="text-sm text-white font-medium">{{ loadingMessage }}</p>
        </div>
      </div>
    </Teleport>

    <!-- Global Toast Notifications -->
    <Teleport to="body">
      <div class="fixed bottom-4 right-4 z-50 space-y-2">
        <TransitionGroup
          name="toast"
          tag="div"
          class="space-y-2"
        >
          <ToastNotification
            v-for="toast in toasts"
            :key="toast.id"
            :toast="toast"
            @close="removeToast(toast.id)"
          />
        </TransitionGroup>
      </div>
    </Teleport>
    
    <!-- Banner PWA Fixo no Topo (NOVO - Sempre visível) -->
    <PWAInstallBanner />
    
    <!-- Global Alert Modal -->
    <GlobalAlert />
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted } from 'vue'
import { useThemeStore } from '@/stores/theme'
import { useAppStore } from '@/stores/app'
import { useBranding } from '@/composables/useBranding'
import ToastNotification from '@/components/ui/ToastNotification.vue'
import GlobalAlert from '@/components/ui/GlobalAlert.vue'
import PWAInstallBanner from '@/components/PWAInstallBanner.vue'
// Stores
const themeStore = useThemeStore()
const appStore = useAppStore()

// Branding
const { applyBranding, updateTitle, updateThemeColor } = useBranding()

// Reactive state
const isLoading = computed(() => appStore.isLoading)
const loadingMessage = computed(() => appStore.loadingMessage)
const toasts = computed(() => appStore.toasts)

// Methods
const removeToast = (id) => {
  appStore.removeToast(id)
}

// Initialize app
const onBrandingUpdated = (event) => {
  const branding = event.detail
  if (!branding) return
  if (branding.panel_name) updateTitle(branding.panel_name)
  if (branding.theme_primary_color) updateThemeColor(branding.theme_primary_color)
}

onMounted(() => {
  themeStore.initTheme()
  applyBranding()
  window.addEventListener('branding:updated', onBrandingUpdated)
})

onUnmounted(() => {
  window.removeEventListener('branding:updated', onBrandingUpdated)
})
</script>

<style scoped>
/* Transition animations */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active {
  transition: transform 0.3s ease-out;
}

.slide-leave-active {
  transition: transform 0.3s ease-in;
}

.slide-enter-from {
  transform: translateX(100%);
}

.slide-leave-to {
  transform: translateX(-100%);
}

/* Toast animations */
.toast-enter-active {
  transition: all 0.3s ease-out;
}

.toast-leave-active {
  transition: all 0.2s ease-in;
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%) scale(0.95);
}

.toast-move {
  transition: transform 0.3s ease;
}
</style>

